import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { getTrainingCamps, getFighters, getFights, trackFeatureUsage } from '../services/api';
import { toast } from 'sonner';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  isSameMonth, 
  isSameDay, 
  parseISO,
  addMonths,
  subMonths,
  isWithinInterval
} from 'date-fns';
import { 
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Plus,
  Swords,
  Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

export const CampCalendarPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [camps, setCamps] = useState([]);
  const [fights, setFights] = useState([]);
  const [fighters, setFighters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    loadData();
    trackFeatureUsage('camp_calendar', 'view').catch(() => {});
  }, []);

  const loadData = async () => {
    try {
      const [campsRes, fightsRes, fightersRes] = await Promise.all([
        getTrainingCamps(),
        getFights(),
        getFighters()
      ]);
      setCamps(campsRes.data);
      setFights(fightsRes.data);
      setFighters(fightersRes.data);
    } catch (error) {
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  const getFighterName = (fighterId) => {
    const fighter = fighters.find(f => f.id === fighterId);
    return fighter?.name || 'Unknown';
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Pad to start on Sunday
  const startDay = monthStart.getDay();
  const paddingDays = Array(startDay).fill(null);

  const getCampsForDay = (date) => {
    return camps.filter(camp => {
      const startDate = parseISO(camp.start_date);
      const endDate = parseISO(camp.end_date);
      return isWithinInterval(date, { start: startDate, end: endDate });
    });
  };

  const getFightsForDay = (date) => {
    return fights.filter(fight => {
      const fightDate = parseISO(fight.fight_date);
      return isSameDay(fightDate, date);
    });
  };

  const getEventsForDay = (date) => {
    const dayCamps = getCampsForDay(date);
    const dayFights = getFightsForDay(date);
    return { camps: dayCamps, fights: dayFights };
  };

  const isToday = (date) => isSameDay(date, new Date());

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const selectedDayEvents = selectedDay ? getEventsForDay(selectedDay) : null;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="camp-calendar-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">
              Camp Calendar
            </h1>
            <p className="text-zinc-400 mt-1">Training camps and fight schedule overview</p>
          </div>
          {isAdmin && (
            <Button 
              onClick={() => navigate('/training-camps/new')}
              className="bg-red-600 hover:bg-red-700 text-white uppercase font-heading font-semibold tracking-wide"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Camp
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-8 bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-6">
              <Button
                variant="ghost"
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                className="text-zinc-400 hover:text-white"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <h2 className="font-heading text-2xl font-bold text-white uppercase tracking-tight">
                {format(currentMonth, 'MMMM yyyy')}
              </h2>
              <Button
                variant="ghost"
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                className="text-zinc-400 hover:text-white"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>

            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-zinc-500 text-sm py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {/* Padding for start of month */}
              {paddingDays.map((_, index) => (
                <div key={`pad-${index}`} className="h-24 bg-zinc-950/30 rounded-sm" />
              ))}

              {/* Days of month */}
              {daysInMonth.map(day => {
                const events = getEventsForDay(day);
                const hasEvents = events.camps.length > 0 || events.fights.length > 0;
                const isSelected = selectedDay && isSameDay(day, selectedDay);

                return (
                  <motion.div
                    key={day.toISOString()}
                    whileHover={{ scale: 1.02 }}
                    onClick={() => setSelectedDay(day)}
                    className={cn(
                      "h-24 p-2 rounded-sm cursor-pointer transition-colors overflow-hidden",
                      isToday(day) ? "bg-red-600/20 border border-red-600" : "bg-zinc-950/50 border border-zinc-800",
                      isSelected && "ring-2 ring-red-500",
                      hasEvents && "hover:border-zinc-600"
                    )}
                  >
                    <p className={cn(
                      "text-sm font-medium",
                      isToday(day) ? "text-red-500" : "text-zinc-400"
                    )}>
                      {format(day, 'd')}
                    </p>
                    
                    <div className="mt-1 space-y-1">
                      {events.camps.slice(0, 2).map(camp => (
                        <div
                          key={camp.id}
                          className="text-xs px-1 py-0.5 bg-blue-600/30 text-blue-400 rounded truncate"
                          title={camp.name}
                        >
                          {camp.name}
                        </div>
                      ))}
                      {events.fights.map(fight => (
                        <div
                          key={fight.id}
                          className="text-xs px-1 py-0.5 bg-red-600/30 text-red-400 rounded truncate flex items-center gap-1"
                          title={`${getFighterName(fight.fighter_id)} vs ${fight.opponent_name}`}
                        >
                          <Swords className="w-3 h-3" />
                          Fight
                        </div>
                      ))}
                      {events.camps.length > 2 && (
                        <p className="text-xs text-zinc-500">+{events.camps.length - 2} more</p>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-6 mt-4 pt-4 border-t border-zinc-800">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-600/30 rounded" />
                <span className="text-zinc-400 text-sm">Training Camp</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-600/30 rounded" />
                <span className="text-zinc-400 text-sm">Fight Day</span>
              </div>
            </div>
          </div>

          {/* Selected Day Details */}
          <div className="lg:col-span-4 bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
            <h3 className="font-heading text-lg font-bold text-white uppercase tracking-tight mb-4">
              {selectedDay ? format(selectedDay, 'EEEE, M/d') : 'Select a Day'}
            </h3>

            {selectedDay && selectedDayEvents ? (
              <div className="space-y-4">
                {/* Camps */}
                {selectedDayEvents.camps.length > 0 && (
                  <div>
                    <p className="text-zinc-500 text-sm mb-2">Training Camps</p>
                    <div className="space-y-2">
                      {selectedDayEvents.camps.map(camp => (
                        <div
                          key={camp.id}
                          onClick={() => navigate(`/training-camps`)}
                          className="p-3 bg-blue-600/10 border border-blue-600/30 rounded-sm cursor-pointer hover:bg-blue-600/20 transition-colors"
                        >
                          <p className="text-white font-medium">{camp.name}</p>
                          <p className="text-zinc-400 text-sm flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {getFighterName(camp.fighter_id)}
                          </p>
                          <p className="text-zinc-500 text-xs mt-1">
                            {format(parseISO(camp.start_date), 'M/d')} - {format(parseISO(camp.end_date), 'M/d')}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Fights */}
                {selectedDayEvents.fights.length > 0 && (
                  <div>
                    <p className="text-zinc-500 text-sm mb-2">Fights</p>
                    <div className="space-y-2">
                      {selectedDayEvents.fights.map(fight => (
                        <div
                          key={fight.id}
                          onClick={() => navigate(`/fights`)}
                          className="p-3 bg-red-600/10 border border-red-600/30 rounded-sm cursor-pointer hover:bg-red-600/20 transition-colors"
                        >
                          <p className="text-white font-medium flex items-center gap-2">
                            <Swords className="w-4 h-4 text-red-500" />
                            {getFighterName(fight.fighter_id)} vs {fight.opponent_name}
                          </p>
                          <p className="text-zinc-400 text-sm">{fight.event_name}</p>
                          <p className="text-zinc-500 text-xs mt-1">{fight.weight_class} • {fight.rounds} rounds</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedDayEvents.camps.length === 0 && selectedDayEvents.fights.length === 0 && (
                  <div className="text-center py-8 text-zinc-500">
                    <CalendarIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No events on this day</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-500">
                <CalendarIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Click a day to see details</p>
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
          <h3 className="font-heading text-lg font-bold text-white uppercase tracking-tight mb-4">
            Active & Upcoming Camps
          </h3>
          
          {camps.filter(c => c.status === 'active' || parseISO(c.start_date) > new Date()).length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {camps
                .filter(c => c.status === 'active' || parseISO(c.start_date) > new Date())
                .slice(0, 6)
                .map(camp => (
                  <div
                    key={camp.id}
                    className="p-4 bg-zinc-950/50 border border-zinc-800 rounded-sm hover:border-zinc-700 transition-colors"
                  >
                    <p className="text-white font-medium">{camp.name}</p>
                    <p className="text-zinc-400 text-sm">{getFighterName(camp.fighter_id)}</p>
                    <p className="text-zinc-500 text-xs mt-2">
                      {format(parseISO(camp.start_date), 'M/d')} - {format(parseISO(camp.end_date), 'M/d/yyyy')}
                    </p>
                    <span className={cn(
                      "inline-block mt-2 text-xs px-2 py-0.5 rounded-sm uppercase",
                      camp.status === 'active' ? "bg-green-600/20 text-green-500" : "bg-blue-600/20 text-blue-400"
                    )}>
                      {camp.status === 'active' ? 'Active' : 'Upcoming'}
                    </span>
                  </div>
                ))}
            </div>
          ) : (
            <p className="text-zinc-500 text-center py-8">No upcoming camps scheduled</p>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CampCalendarPage;
